/**
 * Credits 数值精度工具
 *
 * 所有 getPassCount/getCost 计算的中间值都可能因为 IEEE-754 浮点表示
 * 引入二进制噪声，例如 `0.07 * 100 === 7.000000000000001`、
 * `0.15 * 100 === 15.000000000000002`。
 *
 * 这些噪声若直接送入 `Math.ceil(v * 100) / 100` 会造成"多扣 0.01"的
 * 错误（如 7.01 而非 7），同时也会让 UI 显示出 `7.000000000000001` 这种
 * 不友好的数字。
 *
 * 这里提供统一的工具：
 *  - quantizeCredits：先将浮点噪声修正到 6 位小数（远超 credit 计费精度），
 *    用作 round/ceil 之前的"去噪"步骤；
 *  - roundCredits：UI 展示用，四舍五入到 2 位小数（分位）；
 *  - ceilCredits：实际计费用，向上取整到 2 位小数（分位）。
 *
 * 所有 credits 相关的展示与计费都应统一走这两个函数。
 */

const CREDIT_DECIMALS = 2;
const CREDIT_QUANTIZE = 1e6;

/** 去除 IEEE-754 浮点噪声（保留 6 位小数精度，远高于计费精度）。 */
export function quantizeCredits(value: number): number {
  if (!Number.isFinite(value)) return value;
  return Math.round(value * CREDIT_QUANTIZE) / CREDIT_QUANTIZE;
}

function pow10(decimals: number): number {
  return 10 ** Math.max(0, Math.floor(decimals));
}

/** UI 展示：四舍五入到 `decimals` 位小数（默认 2 位/分），先去噪再 round。 */
export function roundCredits(value: number, decimals: number = CREDIT_DECIMALS): number {
  if (!Number.isFinite(value)) return value;
  const q = quantizeCredits(value);
  const f = pow10(decimals);
  // `q * f` 仍可能引入二次浮点噪声（如 4.9 * 100 = 490.00000000000006），
  // 所以再 quantize 一次再 round/ceil；否则 Math.round / Math.ceil 会被尾数欺骗多算 1 单位。
  return Math.round(quantizeCredits(q * f)) / f;
}

/** 计费：向上取整到 `decimals` 位小数（默认 2 位/分），先去噪再 ceil。 */
export function ceilCredits(value: number, decimals: number = CREDIT_DECIMALS): number {
  if (!Number.isFinite(value)) return value;
  const q = quantizeCredits(value);
  const f = pow10(decimals);
  return Math.ceil(quantizeCredits(q * f)) / f;
}
